// Section 16: Elders last norm. Established by rationing elders short with elders ranked last
// (the Elders group placed after everyone else, or last, in the priority order), 4 uses in a row;
// reset by equal shares or by sparing elders while others go short; lost after 8 seasons without
// reinforcement. Elders left in "everyone else" set no precedent. Effect once established: elders'
// happiness -5; elders emigrate less (nowhere to go) but teach 0.8x (school and apprenticeship
// gains from an elder teacher).
// Constants: config.normRules, config.norms['elders-last'].
import type { Config } from '../../config';
import { mod } from '../../core/modifiers';
import {
  foodPrecedent,
  ranksBefore,
  targetPerson,
  wentShort,
  type RationPrecedent,
} from '../../core/content-helpers';
import { isElder } from '../../core/queries';
import { defineNorm } from '../../core/registry';

/** The Elders group is explicitly ranked last: after everyone else, or the last group. */
function rankedLast(p: RationPrecedent): boolean {
  return (
    p.order.includes('elders') &&
    (ranksBefore(p.order, ['everyone-else'], ['elders']) ||
      p.order[p.order.length - 1] === 'elders')
  );
}

export function eldersLast(config: Config) {
  const c = config.norms['elders-last'];

  return defineNorm({
    id: 'elders-last',
    label: 'Elders last',
    section: 'S16',
    establishAfter: config.normRules.establishAfter,
    decayAfter: config.normRules.decayAfter,
    reinforcedBy(ctx, entry) {
      const p = foodPrecedent(ctx, entry);
      if (p === null || p.policy === 'equal') return false;
      return rankedLast(p) && wentShort(p, ['elders'], true);
    },
    contradictedBy(ctx, entry) {
      const p = foodPrecedent(ctx, entry);
      if (p === null) return false;
      if (p.policy === 'equal') return true;
      return p.short !== null && p.short.length > 0 && !wentShort(p, ['elders'], false);
    },
    modifierTargets: ['happinessTarget', 'emigrationPressure', 'skillGain'],
    modifiers(ctx, target) {
      if (!ctx.state.norms['elders-last'].established) return [];
      if (target.kind === 'skillGain') {
        const teacher =
          target.teacherId === undefined ? undefined : ctx.state.people[target.teacherId];
        return teacher !== undefined && isElder(ctx.cfg, teacher)
          ? [mod.mul('norm:elders-last', c.elderTeachMul, 'taught by an elder under elders last')]
          : [];
      }
      const p = targetPerson(ctx, target);
      if (p === null || !isElder(ctx.cfg, p)) return [];
      if (target.kind === 'happinessTarget')
        return [mod.add('norm:elders-last', c.elderHappiness, 'elders last')];
      if (target.kind === 'emigrationPressure')
        return [mod.mul('norm:elders-last', c.elderEmigrationMul, 'nowhere to go')];
      return [];
    },
  });
}
